import { createActionHelpers } from 'vuex-loading';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

const state = {
    products: [],
    inProducts: [],
    selectors: [],
    stockMovement: {
        reference_id: null,
        outlet: {
            name: null,
        },
        stock_movement: {
            id: null,
            outlet: {
                name: null,
            },
        },
    },
    inStockMovement: {
        reference_id: null,
        outlet: {
            name: null,
        },
        stock_movement: {
            id: null,
            outlet: {
                name: null,
            },
        },
    },
};

const getters = {
    stockMovement: state => state.stockMovement,

    hasFinalized: state => state.stockMovement.status === 2,

    inHasFinalized: state => state.inStockMovement.status === 2,

    selectors: state => state.selectors,

    completionStatus: state => {
        if (
            state.stockMovement.status === 2 ||
            state.inStockMovement.status === 2
        ) {
            return 'Completed';
        }

        return 'In Progress';
    },

    products: state =>
        state.products.map((p, i) => {
            p.rowIndex = i + 1;

            p.name = p.stock.product.name;
            p.code = p.stock.product.code;
            p.total_quantity = p.total_quantity
                ? Math.abs(p.total_quantity)
                : null;

            return p;
        }),

    inProducts: state =>
        state.inProducts.map((p, i) => {
            p.rowIndex = i + 1;

            p.name = p.stock.product.name;
            p.code = p.stock.product.code;
            p.supplier_price = parseFloat(p.supplier_price).toFixed(4);

            return p;
        }),

    findStockIndex: getters => stockId => {
        return getters.products.findIndex(s => s.stock_id === stockId);
    },

    inStockMovement: state => state.inStockMovement,
};

const mutations = {
    setStockMovement(state, data) {
        state.stockMovement = data;
    },

    setInStockMovement(state, data) {
        state.inStockMovement = data;
    },

    setProducts(state, data) {
        state.products = data;
    },

    setSelectors(state, data) {
        state.selectors = data;
    },

    setRemoveProduct(state, index) {
        state.products.splice(index, 1);
    },

    setAddProduct(state, data) {
        state.products.push(data);
    },

    setUpdateProduct(state, { index, key, value }) {
        state.products[index][key] = value;
    },

    merge(state, data) {
        state.stockMovement.products = data;
    },

    inMerge(state, data) {
        state.inStockMovement.products = data;
    },

    setInProducts(state, data) {
        state.inProducts = data;
    },

    setResetState(state) {
        state.products = [];
        state.inProducts = [];
        state.stockMovement = {
            reference_id: null,
            outlet: {
                name: null,
            },
            stock_movement: {
                id: null,
                outlet: {
                    name: null,
                },
            },
        };
        state.inStockMovement = {
            reference_id: null,
            outlet: {
                name: null,
            },
            stock_movement: {
                id: null,
                outlet: {
                    name: null,
                },
            },
        };
    },
};

const actions = {
    async createTransferOut({ commit, dispatch }, params) {
        const url = Laroute.route('inventory.stock-transfer.out.create');

        startLoading(dispatch, 'save-stock-transfer');

        try {
            const { data } = await axios.post(url, params);
            endLoading(dispatch, 'save-stock-transfer');
            return data;
        } catch (e) {
            endLoading(dispatch, 'save-stock-transfer');
            throw e;
        }
    },

    async getTransferOutInfo({ commit, dispatch }, id) {
        const url = Laroute.route('inventory.stock-transfer.out.info', { id });

        startLoading(dispatch, 'fetch-stock-transfer');

        try {
            const { data } = await axios.get(url);
            commit('setStockMovement', data);
            endLoading(dispatch, 'fetch-stock-transfer');
            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-stock-transfer');
            throw e;
        }
    },

    async getTransferOutProductList({ commit, dispatch }, id) {
        const url = Laroute.route('inventory.stock-transfer.out.product.list', {
            id,
        });

        startLoading(dispatch, 'fetch-product-list');

        try {
            const { data } = await axios.get(url);
            commit('setProducts', data);
            endLoading(dispatch, 'fetch-product-list');
            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-product-list');
            throw e;
        }
    },

    async getTransferInInfo({ commit, dispatch }, id) {
        const url = Laroute.route('inventory.stock-transfer.in.info', { id });

        startLoading(dispatch, 'fetch-stock-transfer');

        try {
            const { data } = await axios.get(url);
            commit('setInStockMovement', data);
            endLoading(dispatch, 'fetch-stock-transfer');
            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-stock-transfer');
            throw e;
        }
    },

    async getTransferInProductList({ commit, dispatch }, id) {
        const url = Laroute.route('inventory.stock-transfer.in.product.list', {
            id,
        });

        startLoading(dispatch, 'fetch-in-product-list');

        try {
            const { data } = await axios.get(url);
            commit('setInProducts', data);
            endLoading(dispatch, 'fetch-in-product-list');
            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-in-product-list');
            throw e;
        }
    },

    async saveStockTransfer({ commit, dispatch, state }) {
        const url = Laroute.route('inventory.stock-transfer.out.update');

        startLoading(dispatch, 'save-stock-transfer');

        commit('merge', state.products);

        try {
            const { data } = await axios.put(url, state.stockMovement);

            commit('setStockMovement', data.stockMovement);
            commit('setProducts', data.products);
            commit('setInProducts', data.inProducts);

            endLoading(dispatch, 'save-stock-transfer');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-stock-transfer');
            throw e;
        }
    },

    async commitStockTransfer({ commit, dispatch, state }) {
        const url = Laroute.route('inventory.stock-transfer.out.commit');

        startLoading(dispatch, 'save-stock-transfer');

        commit('merge', state.products);

        try {
            const { data } = await axios.put(url, state.stockMovement);

            commit('setStockMovement', data.stockMovement);
            commit('setInProducts', data.inProducts);

            endLoading(dispatch, 'save-stock-transfer');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-stock-transfer');
            throw e;
        }
    },

    async commitStockAccept({ commit, dispatch, state }) {
        const url = Laroute.route('inventory.stock-transfer.in.commit');

        startLoading(dispatch, 'save-stock-transfer');

        commit('merge', state.inProducts);

        try {
            const { data } = await axios.put(url, state.inStockMovement);

            commit('setInStockMovement', data.inStockMovement);
            commit('setInProducts', data.inProducts);

            endLoading(dispatch, 'save-stock-transfer');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-stock-transfer');
            throw e;
        }
    },

    async getSelectors({ commit, dispatch }, params = {}) {
        const url = Laroute.route('inventory.stock-transfer.selector');

        const options = {
            params: Object.assign(
                {
                    query: null,
                    limit: 15,
                },
                params,
            ),
        };

        startLoading(dispatch, 'fetch-selectors');

        try {
            const { data } = await axios.get(url, options);
            commit('setSelectors', data);
            endLoading(dispatch, 'fetch-selectors');
            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-selectors');
            throw e;
        }
    },

    removeProduct({ commit, getters }, stockId) {
        const index = getters.findStockIndex(stockId);

        if (index >= 0) {
            commit('setRemoveProduct', index);
            // EventStock.$emit('remove-stock-id', stock_id);
        }
    },

    addProduct({ commit, getters }, stock) {
        const product = {
            stock: {
                product: stock.product,
            },
            stock_id: stock.id,
            unique_id: stock.unique_id,
            quantity_before: stock.quantity,
            total_quantity: null,
        };

        commit('setAddProduct', product);
    },

    updateProduct({ commit, getters }, { stockId, key, value }) {
        const index = getters.findStockIndex(stockId);
        commit('setUpdateProduct', { index, key, value });
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
